import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Flex,
  Text,
  Button,
  Badge,
  Spinner,
  Alert,
  AlertIcon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  HStack,
  useToast,
  useColorModeValue,
} from "@chakra-ui/react";
import { ArrowBackIcon, DeleteIcon, CheckIcon } from "@chakra-ui/icons";
import { FiRefreshCw } from "react-icons/fi";
import { milestoneService } from "../services/edosService";
import api from "../services/api";

const STATUS_COLORS = {
  pending: "gray",
  in_progress: "blue",
  completed: "teal",
  missed: "red",
  cancelled: "orange",
};

const normalizeTicketRows = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.tickets)) return payload.data.tickets;
  if (Array.isArray(payload?.tickets)) return payload.tickets;
  return [];
};

export default function MilestoneDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const bg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const headerBg = useColorModeValue("gray.50", "gray.700");

  const [milestone, setMilestone] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const milestoneRes = await milestoneService.getById(id);
      setMilestone(milestoneRes?.data?.data || milestoneRes?.data);

      const allRows = [];
      let page = 1;
      let totalPages = 1;
      do {
        const res = await api.get("/api/tickets", {
          params: {
            page,
            limit: 100,
            milestoneId: id,
            _viewMode: "all",
            sortBy: "createdAt",
            sortOrder: "desc",
          },
        });
        const payload = res?.data;
        allRows.push(...normalizeTicketRows(payload));
        totalPages = Number(
          payload?.totalPages || payload?.data?.totalPages || 1,
        );
        page += 1;
      } while (page <= totalPages && page <= 50);
      setTickets(allRows);
    } catch {
      toast({ title: "Failed to load milestone details", status: "error" });
      setMilestone(null);
      setTickets([]);
    } finally {
      setLoading(false);
    }
  }, [id, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const handleComplete = async () => {
    setActionLoading(true);
    try {
      await milestoneService.complete(id);
      toast({ title: "Milestone completed", status: "success" });
      load();
    } catch (err) {
      toast({
        title: err?.response?.data?.message || "Failed to complete milestone",
        status: "error",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this milestone?")) return;
    setActionLoading(true);
    try {
      await milestoneService.remove(id);
      toast({ title: "Milestone deleted", status: "success" });
      navigate("/edos/milestones");
    } catch (err) {
      toast({
        title: err?.response?.data?.message || "Failed to delete milestone",
        status: "error",
      });
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" minH="60vh">
        <Spinner size="xl" color="teal.500" />
      </Flex>
    );
  }

  if (!milestone) {
    return (
      <Box p={6}>
        <Alert status="error">
          <AlertIcon />
          Milestone not found.
        </Alert>
        <Button
          mt={4}
          leftIcon={<ArrowBackIcon />}
          onClick={() => navigate("/edos/milestones")}
        >
          Back to Milestones
        </Button>
      </Box>
    );
  }

  return (
    <Box p={6}>
      <Flex justify="space-between" align="center" mb={6} wrap="wrap" gap={3}>
        <HStack>
          <Button
            leftIcon={<ArrowBackIcon />}
            variant="ghost"
            onClick={() => navigate("/edos/milestones")}
          >
            Milestones
          </Button>
          <Text fontSize="2xl" fontWeight="bold">
            {milestone.milestoneName}
          </Text>
          <Badge colorScheme={STATUS_COLORS[milestone.status] || "gray"}>
            {milestone.status?.replace(/_/g, " ")}
          </Badge>
        </HStack>
        <HStack>
          <Button leftIcon={<FiRefreshCw />} variant="outline" onClick={load}>
            Refresh
          </Button>
          {milestone.status !== "completed" && (
            <Button
              leftIcon={<CheckIcon />}
              colorScheme="teal"
              isLoading={actionLoading}
              onClick={handleComplete}
            >
              Mark Complete
            </Button>
          )}
          <Button
            leftIcon={<DeleteIcon />}
            colorScheme="red"
            variant="outline"
            isLoading={actionLoading}
            onClick={handleDelete}
          >
            Delete
          </Button>
        </HStack>
      </Flex>

      <Box
        p={4}
        bg={bg}
        border="1px"
        borderColor={borderColor}
        borderRadius="md"
        mb={6}
      >
        <Text fontSize="sm" color="gray.500">
          Due Date
        </Text>
        <Text fontWeight="semibold">
          {milestone.dueDate
            ? new Date(milestone.dueDate).toLocaleDateString()
            : "-"}
        </Text>
        {milestone.description && (
          <>
            <Text mt={3} fontSize="sm" color="gray.500">
              Description
            </Text>
            <Text>{milestone.description}</Text>
          </>
        )}
      </Box>

      <Box
        bg={bg}
        border="1px"
        borderColor={borderColor}
        borderRadius="md"
        overflow="hidden"
      >
        <Box
          px={4}
          py={3}
          bg={headerBg}
          borderBottom="1px"
          borderColor={borderColor}
        >
          <Text fontWeight="bold">Linked Tickets ({tickets.length})</Text>
        </Box>
        {tickets.length === 0 ? (
          <Alert status="info" m={4}>
            <AlertIcon />
            No tickets linked to this milestone.
          </Alert>
        ) : (
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                <Th>Ticket</Th>
                <Th>Title</Th>
                <Th>Status</Th>
                <Th>Priority</Th>
              </Tr>
            </Thead>
            <Tbody>
              {tickets.map((ticket) => (
                <Tr
                  key={ticket._id}
                  _hover={{ bg: headerBg, cursor: "pointer" }}
                  onClick={() => navigate(`/Ticketing/${ticket._id}`)}
                >
                  <Td>{ticket.ticketCode || ticket.ticketId || "-"}</Td>
                  <Td>
                    <Text noOfLines={1}>{ticket.title || "-"}</Text>
                  </Td>
                  <Td>
                    <Badge colorScheme="gray">
                      {String(ticket.workflowState || "open").replace(
                        /_/g,
                        " ",
                      )}
                    </Badge>
                  </Td>
                  <Td>
                    <Badge colorScheme="orange">{ticket.priority || "-"}</Badge>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>
    </Box>
  );
}
